import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { agentsService } from '../services/api';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

const PoliciesManager = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [vectorStoreInfo, setVectorStoreInfo] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [hasVectorStore, setHasVectorStore] = useState(false);

  // Load available clients on mount
  const loadClients = useCallback(async () => {
    try {
      const [storesResponse, clientsResponse] = await Promise.all([
        agentsService.getAllVectorStores().catch(() => ({ vectorStores: [] })),
        agentsService.getAvailableClients()
      ]);

      const storeMap = new Map(
        (storesResponse.vectorStores || []).map((vectorStore) => [vectorStore.client_slug, vectorStore])
      );

      const mergedClients = (clientsResponse.clients || []).map((client) => ({
        client_slug: client.client_slug,
        client_name: client.client_name,
        display_name: client.client_name,
        ...(storeMap.get(client.client_slug) || {})
      }));

      setClients(mergedClients);

      const preferredClient =
        user?.client_slug && mergedClients.some((client) => client.client_slug === user.client_slug)
          ? user.client_slug
          : mergedClients[0]?.client_slug;

      if (preferredClient && !selectedClient) {
        setSelectedClient(preferredClient);
      }
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    }
  }, [selectedClient, user?.client_slug]);

  const loadClientFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await agentsService.getClientFiles(selectedClient);
      setVectorStoreInfo(response.vectorStoreInfo);
      setFiles(response.files || []);
      setHasVectorStore(Boolean(response.hasVectorStore));
    } catch (error) {
      toast.error(`Erro ao carregar arquivos: ${error.response?.data?.error || error.message}`);
      setVectorStoreInfo(null);
      setFiles([]);
      setHasVectorStore(false);
    } finally {
      setLoadingFiles(false);
    }
  }, [selectedClient]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (selectedClient) {
      loadClientFiles();
    }
  }, [loadClientFiles, selectedClient]);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setUploadedFiles(selectedFiles);
  };

  const handleUpload = async () => {
    if (!uploadedFiles.length) {
      toast.warning('Selecione pelo menos um arquivo');
      return;
    }

    setUploading(true);
    let successCount = 0;

    for (let i = 0; i < uploadedFiles.length; i++) {
      try {
        const file = uploadedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('client_slug', selectedClient);

        await agentsService.uploadPolicyFile(selectedClient, formData, (progressEvent) => {
          const currentFileProgress = progressEvent.total
            ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
            : 0;
          const overallProgress = Math.round(
            ((i + currentFileProgress / 100) / uploadedFiles.length) * 100
          );
          setUploadProgress(overallProgress);
        });

        successCount++;
        setUploadProgress(Math.round(((i + 1) / uploadedFiles.length) * 100));
        toast.success(`${file.name} enviado com sucesso`);
      } catch (error) {
        const detail = error.response?.data?.error || error.message;
        toast.error(`Erro ao enviar ${uploadedFiles[i].name}: ${detail}`);
      }
    }

    setUploading(false);
    setUploadProgress(0);
    setUploadedFiles([]);
    document.getElementById('fileInput').value = '';

    if (successCount > 0) {
      toast.info(`${successCount} arquivo(s) enviado(s) com sucesso`);
      setHasVectorStore(true);
      await loadClientFiles(); // Reload files list
    }
  };

  const handleDeleteFile = async (fileId, filename) => {
    if (!window.confirm(`Tem certeza que deseja remover "${filename}"?`)) {
      return;
    }

    try {
      await agentsService.deleteFile(fileId, selectedClient);
      toast.success('Arquivo removido com sucesso');
      await loadClientFiles();
    } catch (error) {
      toast.error(`Erro ao remover arquivo: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Digite uma query para buscar');
      return;
    }

    if (!hasVectorStore) {
      toast.info('Este cliente ainda não possui vector store. Envie documentos primeiro.');
      return;
    }

    setSearching(true);
    try {
      const response = await agentsService.searchVectorStore(selectedClient, searchQuery);
      setSearchResults(response.result);
      toast.success('Busca realizada com sucesso');
    } catch (error) {
      toast.error(`Erro ao buscar: ${error.response?.data?.error || error.message}`);
      setSearchResults(null);
    } finally {
      setSearching(false);
    }
  };

  const getClientDisplayName = (slug) => {
    const client = clients.find(c => c.client_slug === slug);
    return client?.display_name || client?.client_name || slug;
  };

  return (
    <div className="rf-screen">
      <div style={{ maxWidth: 1100, width: '100%' }}>
        <div className="rf-screen__head" style={{ marginBottom: 18 }}>
          <div>
            <h1 className="rf-screen__title">Políticas de Clientes</h1>
            <p className="rf-screen__sub">
              Base de conhecimento (RAG) por cliente. Os documentos indexados alimentam a IA
              na geração das investigações.
            </p>
          </div>
        </div>

        {/* Client Selector */}
        <Card className="rf-card" style={{ marginBottom: 16 }}>
          <Label htmlFor="clientSelect" className="rf-label" style={{ marginBottom: 8, display: 'block' }}>
            Cliente
          </Label>
          <select
            id="clientSelect"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="rf-select"
          >
            <option value="">-- Selecione um cliente --</option>
            {clients.map(client => (
              <option key={client.client_slug} value={client.client_slug}>
                {getClientDisplayName(client.client_slug)}
              </option>
            ))}
          </select>
        </Card>

        {selectedClient && (
          <>
            {/* Vector Store Status */}
            <Card className="rf-card" style={{ marginBottom: 16 }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="rf-card__title" style={{ marginBottom: 6 }}>
                    {hasVectorStore ? 'Base indexada ativa' : 'Base ainda não criada'}
                  </h3>
                  <p className="text-gray-700">
                    {hasVectorStore
                      ? <>📄 <strong>{files.length} documento(s)</strong> indexado(s)</>
                      : 'Este cliente ainda não tem base indexada. Isso não bloqueia o sistema; a IA seguirá sem RAG até o primeiro upload.'}
                  </p>
                  {vectorStoreInfo?.last_synced_at && (
                    <p className="text-gray-600 text-sm mt-1">
                      Última atualização: {new Date(vectorStoreInfo.last_synced_at).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
                <div className="text-4xl">🗄️</div>
              </div>
            </Card>

            {/* Indexed Documents */}
            <Card className="rf-card" style={{ marginBottom: 16 }}>
              <h3 className="rf-card__title" style={{ marginBottom: 14 }}>Documentos indexados</h3>

              {loadingFiles ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600 mt-2">Carregando arquivos...</p>
                </div>
              ) : files.length === 0 ? (
                <p className="text-gray-500 py-4">
                  {hasVectorStore
                    ? 'Nenhum documento indexado ainda. Envie o primeiro!'
                    : 'Sem vector store e sem documentos ainda. O primeiro upload criará a base automaticamente.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {files.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-xl">📄</span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{file.filename}</p>
                          <p className="text-sm text-gray-600">
                            {file.filesize ? (file.filesize / 1024 / 1024).toFixed(2) + ' MB' : 'Tamanho desconhecido'} •
                            {' '}{new Date(file.uploaded_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDeleteFile(file.openai_file_id, file.filename)}
                        className="rf-btn rf-btn--ghost" style={{ padding: '6px 14px', fontSize: '.78rem' }}
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Upload Section */}
            <Card className="rf-card" style={{ marginBottom: 16 }}>
              <h3 className="rf-card__title" style={{ marginBottom: 10 }}>Enviar documentos</h3>
              <p className="text-gray-600 text-sm mb-4">
                Formatos aceitos: PDF, DOCX, DOC, TXT (máximo 50 MB por arquivo)
              </p>

              <div className="border-2 border-dashed border-blue-400 rounded-lg p-6 text-center bg-white mb-4">
                <input
                  id="fileInput"
                  type="file"
                  multiple
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label htmlFor="fileInput" className="cursor-pointer">
                  <div className="text-4xl mb-2">📁</div>
                  <p className="text-gray-700 font-medium">
                    Arrastar arquivos aqui ou <span className="text-blue-600 underline">selecionar</span>
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    Múltiplos arquivos permitidos
                  </p>
                </label>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {uploadedFiles.length} arquivo(s) selecionado(s):
                  </p>
                  <ul className="space-y-1">
                    {uploadedFiles.map((file, idx) => (
                      <li key={idx} className="text-sm text-gray-600">
                        ✓ {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uploading && (
                <div className="mb-4">
                  <div className="w-full bg-gray-300 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{uploadProgress}% concluído</p>
                </div>
              )}

              <Button
                onClick={handleUpload}
                disabled={uploading || uploadedFiles.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Enviando...' : hasVectorStore ? 'Enviar Documentos' : 'Criar Base e Enviar Documentos'}
              </Button>
            </Card>

            {/* RAG Test Section */}
            <Card className="p-6 border border-purple-300 bg-purple-50 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🔍 Testar RAG (Busca)
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Digite uma pergunta ou termo para ver o que a IA recupera dos documentos indexados.
              </p>

              <div className="flex gap-3 mb-4">
                <Input
                  type="text"
                  placeholder="Ex: O que é conflito de interesses? ou Qual é a política de horas extras?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <Button
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim() || !hasVectorStore}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searching ? '🔄 Buscando...' : '🔍 Buscar'}
                </Button>
              </div>

              {searchResults && (
                <div className="mt-4 p-4 bg-white border border-purple-200 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Resultado da Busca:</h4>
                  <div className="bg-gray-50 p-3 rounded border border-gray-300 max-h-48 overflow-y-auto">
                    <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                      {searchResults.text || 'Nenhum resultado encontrado nos documentos.'}
                    </p>
                  </div>
                  {searchResults.citations && searchResults.citations.length > 0 && (
                    <div className="mt-3 text-xs text-gray-600">
                      <p className="font-medium">Fontes:</p>
                      {searchResults.citations.map((c, idx) => (
                        <p key={idx}>• {c.source || c.file || 'Documento indexado'}</p>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    Tokens usados: {searchResults.tokenUsage}
                  </p>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default PoliciesManager;
